import { z } from 'zod';

const Cash = z.object({
  id: z.string().nullish(),
  t3: z.number(),
  t2Date: z.string(),
  t2: z.number(),
  t1Date: z.string(),
  t1: z.number(),
  creditMoneyRemain: z.number(),
  creditBlockedMoney: z.number(),
  creditMoney: z.number(),
  nonWithdrawableMoneyRemain: z.number(),
  nonWithdrawableBlockedMoney: z.number(),
  withdrawableBlockedMoney: z.number(),
  withdrawableMoneyRemain: z.number(),
  totalAsset: z.number(),
  percentageProfit: z.number(),
  profit: z.number(),
  buyingPower: z.number(),
  blockedValue: z.number(),
  t3Date: z.string(),
  dealerCreditBlockedMoney: z.number(),
  usedCredit: z.number(),
});

const CreateTokenRequest = z.object({
  totalAmount: z.number(),
  bankAccountUsageType: z.number().nullable(),
  deviceType: z.number().nullable(),
});

const CreateBankAccountRequest = z.object({
  iBan: z.string().nullable(),
});

const CreateTokenResponse = z.object({
  token: z.string(),
  mobile: z.string(),
});

const CreateWithdrawResponse = z.object({
  personageOwnerId: z.number(),
  entryUserId: z.number(),
  sourceAccountUsageType: z.number(),
  destinationAccountId: z.number(),
  requestWithdrawalValue: z.number(),
  description: z.string().nullable(),
  dueDate: z.string().datetime().nullable(),
  timeValidityType: z.number(),
  requestDepositDate: z.string().datetime(),
});

const PayableAmountItem = z.object({
  amount: z.number(),
  date: z.string(),
  isEnabled: z.boolean(),
  persianDate: z.string(),
  tDate: z.date(),
});

const PayableAmounts = z.object({
  t0: PayableAmountItem,
  t1: PayableAmountItem,
  t2: PayableAmountItem,
  t3: PayableAmountItem,
});

const EPayment = z.object({
  id: z.number(),
  bankTitle: z.string().nullable(),
  amount: z.number(),
  resNum: z.string().nullable(),
  isVerified: z.boolean(),
  entryDate: z.string(),
  bankAccountUsageTypeTitle: z.string().nullable(),
  isIncreaseBalanceInCas: z.boolean().nullable(),
  ePaymentTransactionStateTitle: z.string().nullable(),
  syncWithAccounting: z.boolean(),
  traceNo: z.string().nullable(),
});

const EPaymentsResult = z.object({
  totalCount: z.number(),
  items: z.array(EPayment).nullable(),
});

const WithdrawalItem = z.object({
  id: z.number(),
  requestId: z.number(),
  destinationAccount: z.string().nullable(),
  requestDepositDate: z.string().nullable(),
  requestDepositStringDate: z.string().nullable(),
  requestWithdrawalValue: z.number(),
  requestStringDate: z.string().nullable(),
  requestEntryDate: z.string(),
  requestSerialNumber: z.string().nullable(),
  requestStateId: z.number(),
  requestState: z.string().nullable(),
  requestGatewayId: z.number(),
  requestDescription: z.string().nullable(),
  thirdPartyPursuitNum: z.number().nullable(),
  isPayfast: z.boolean(),
});

const LastWithdrawalQuery = z.object({
  totalCount: z.number(),
  items: WithdrawalItem.array().nullable(),
});

const BankAccount = z.object({
  accountId: z.number(),
  accountCode: z.string(),
  title: z.string(),
  bankTitle: z.string(),
  bankBranchName: z.string(),
  sheba: z.string(),
});

const BankAccountsQuery = BankAccount.array();

const Deposit = z.object({
  depositValue: z.number(),
  requestStateId: z.number(),
  requestState: z.string().nullable(),
  trackingNumber: z.string().nullable(),
  requestGatewayId: z.number(),
  requestId: z.number(),
  requestSerialNumber: z.string().nullable(),
  depositedStringDate: z.string().nullable(),
  depositedDate: z.string(),
  requestEntryDate: z.string(),
  requestStringDate: z.string().nullable(),
  sourceAccount: z.string().nullable(),
  depositAttachmentId: z.number().nullable(),
});

const DepositsQuery = z.object({
  totalCount: z.number(),
  items: Deposit.array().nullable(),
});

const DepositReceiptAttachmentQuery = z.object({
  data: z.string().nullable(),
});

const Turnover = z.object({
  entryDate: z.string().nullable(),
  operationType: z.string().nullable(),
  operationTypeId: z.number(),
  tradeQuantity: z.number(),
  tradePrice: z.number(),
  instrumentTitle: z.string().nullable(),
  debit: z.number(),
  credit: z.number(),
  remaining: z.number(),
  type: z.string().nullable(),
  instrumentIdentification: z.string().nullable(),
  instrumentId: z.number().nullable(),
  instrumentName: z.string().nullable(),
});

const TurnoverResult = z.object({
  totalCount: z.number(),
  items: z.array(Turnover).nullable(),
  remaining: z.number(),
  beginningOfPeriod: z.number(),
  type: z.string().nullable(),
});

const TurnoverReportResult = z.object({
  content: z.string().nullable(),
  name: z.string().nullable(),
  contentType: z.string().nullable(),
});

const RegisterReceiptRequest = z.object({
  sourceAccountId: z.number(),
  depositValue: z.number(),
  trackingNumber: z.string(),
  depositedDate: z.string(),
  description: z.string().nullish(),
  attachment: z.string(),
  source: z.number().nullable(),
});

const DirectDebitRequest = z.object({
  traceId: z.string(),
  amount: z.number(),
});

const DirectDebitsItem = z.object({
  bank: z.string(),
  enabled: z.boolean(),
  id: z.number(),
  traceId: z.string(),
  uId: z.string(),
  withdrawCeiling: z.number(),
});

const DirectDebitsQuery = z.array(DirectDebitsItem);

type Cash = z.infer<typeof Cash>;
type DirectDebitRequest = z.infer<typeof DirectDebitRequest>;
type DirectDebitsItem = z.infer<typeof DirectDebitsItem>;
type DirectDebitsQuery = z.infer<typeof DirectDebitsQuery>;
type CreateTokenRequest = z.infer<typeof CreateTokenRequest>;
type CreateBankAccountRequest = z.infer<typeof CreateBankAccountRequest>;
type CreateTokenResponse = z.infer<typeof CreateTokenResponse>;
type CreateWithdrawResponse = z.infer<typeof CreateWithdrawResponse>;
type PayableAmounts = z.infer<typeof PayableAmounts>;
type PayableAmountItem = z.infer<typeof PayableAmountItem>;
type EPayment = z.infer<typeof EPayment>;
type EPaymentsResult = z.infer<typeof EPaymentsResult>;
type LastWithdrawalQuery = z.infer<typeof LastWithdrawalQuery>;
type WithdrawalItem = z.infer<typeof WithdrawalItem>;
type BankAccountsQuery = z.infer<typeof BankAccountsQuery>;
type Deposit = z.infer<typeof Deposit>;
type DepositsQuery = z.infer<typeof DepositsQuery>;
type DepositReceiptAttachmentQuery = z.infer<
  typeof DepositReceiptAttachmentQuery
>;
type Turnover = z.infer<typeof Turnover>;
type TurnoverResult = z.infer<typeof TurnoverResult>;
type TurnoverReportResult = z.infer<typeof TurnoverReportResult>;
type RegisterReceiptRequest = z.infer<typeof RegisterReceiptRequest>;

export {
  Cash,
  CreateTokenRequest,
  CreateTokenResponse,
  CreateWithdrawResponse,
  PayableAmounts,
  PayableAmountItem,
  EPayment,
  EPaymentsResult,
  LastWithdrawalQuery,
  WithdrawalItem,
  BankAccountsQuery,
  Deposit,
  DepositsQuery,
  DepositReceiptAttachmentQuery,
  Turnover,
  TurnoverResult,
  TurnoverReportResult,
  CreateBankAccountRequest,
  RegisterReceiptRequest,
  DirectDebitRequest,
  DirectDebitsItem,
  DirectDebitsQuery,
};
